import React, { useState, useEffect } from 'react';
import { VideoProgress, videosAPI } from '../../services/api';
import VideoPlayer from './VideoPlayer';
import {
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Spinner, Badge, Button, EmptyState, PageHero } from '../ui';


const TrainingDashboard: React.FC = () => {
  const [progress, setProgress] = useState<VideoProgress[]>([]);
  const [trainingStatus, setTrainingStatus] = useState<{ trainingCompleted: boolean } | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching training data...');
      const [progressData, statusData] = await Promise.all([
        videosAPI.getMyProgress(),
        videosAPI.getTrainingStatus(),
      ]);
      console.log('Progress data:', progressData);
      console.log('Status data:', statusData);

      // Debug each video in the progress data
      progressData.forEach((progress, index) => {
        console.log(`Video ${index + 1}:`, {
          progressId: progress.id,
          videoId: progress.videoId,
          isCompleted: progress.isCompleted,
          video: progress.video
        });

        if (progress.video) {
          console.log(`  Video details:`, {
            id: progress.video.id,
            title: progress.video.title,
            filename: progress.video?.filename,
            streamUrl: videosAPI.getStreamUrl(progress.video.id)
          });
        }
      });

      setProgress(progressData);
      setTrainingStatus(statusData);
    } catch (err: any) {
      console.error('Error fetching training data:', err);
      setError(err.response?.data?.message || 'Failed to fetch training data');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoComplete = async (videoId: number) => {
    try {
      await videosAPI.updateProgress(videoId, true);
      await fetchData(); // Refresh data to update completion status
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update progress');
    }
  };

  const completedVideos = progress.filter(p => p.isCompleted).length;
  const totalVideos = progress.length;
  const completionPercentage = totalVideos > 0 ? (completedVideos / totalVideos) * 100 : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8" data-tour="page-content">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Training Center"
        subtitle="Complete all required training videos to access the sales platform"
        icon={<AcademicCapIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="pink"
      />

      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50 mb-6">
          <CardBody className="p-4">
            <p className="text-error-700">{error}</p>
          </CardBody>
        </Card>
      )}

      {/* No videos message */}
      {!loading && progress.length === 0 && !error && (
        <Card variant="bordered" className="border-warning-200 bg-warning-50 mb-6">
          <CardBody className="p-8 text-center">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-warning-400 mb-4" />
            <h3 className="text-lg font-medium text-warning-800 mb-2">No Training Videos Available</h3>
            <p className="text-warning-700">
              No training videos have been uploaded yet. Please contact your administrator.
            </p>
          </CardBody>
        </Card>
      )}

      {/* Training Status Card */}
      <Card variant="elevated" className="mb-8">
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${trainingStatus?.trainingCompleted ? 'bg-success-100' : 'bg-warning-100'
                }`}>
                {trainingStatus?.trainingCompleted ? (
                  <CheckCircleIcon className="h-8 w-8 text-success-600" />
                ) : (
                  <ClockIcon className="h-8 w-8 text-warning-600" />
                )}
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-neutral-900">
                  Training Status
                </h3>
                <p className={`text-sm ${trainingStatus?.trainingCompleted ? 'text-success-600' : 'text-warning-600'
                  }`}>
                  {trainingStatus?.trainingCompleted
                    ? 'Training Completed - You can now access all platform features!'
                    : 'Training In Progress - Complete all videos to unlock the platform'
                  }
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-neutral-900">
                {completedVideos}/{totalVideos}
              </div>
              <div className="text-sm text-neutral-500">Videos Completed</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-neutral-600 mb-1">
              <span>Progress</span>
              <span>{completionPercentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${trainingStatus?.trainingCompleted ? 'bg-success-600' : 'bg-primary-600'
                  }`}
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Video List */}
      <Card variant="elevated">
        <CardHeader
          title="Training Videos"
          icon={<AcademicCapIcon className="h-5 w-5" />}
        />
        <div className="divide-y divide-neutral-200">
          {progress.map((videoProgress, index) => {
            const video = videoProgress.video;
            if (!video) return null;

            const isLocked = index > 0 && !progress[index - 1]?.isCompleted;

            return (
              <div key={video.id} className={`p-6 ${isLocked ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${videoProgress.isCompleted
                      ? 'bg-success-100'
                      : isLocked
                        ? 'bg-neutral-100'
                        : 'bg-primary-100'
                      }`}>
                      {videoProgress.isCompleted ? (
                        <CheckCircleIcon className="h-6 w-6 text-success-600" />
                      ) : isLocked ? (
                        <LockClosedIcon className="h-6 w-6 text-neutral-400" />
                      ) : (
                        <PlayIcon className="h-6 w-6 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-neutral-900">{video.title}</h4>
                      {video.description && (
                        <p className="text-sm text-neutral-500 mt-1">{video.description}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-500">
                        <span>Video {index + 1} of {totalVideos}</span>
                        {video.duration && (
                          <span>{Math.ceil(video.duration / 60)} minutes</span>
                        )}
                        {video.isRequired && (
                          <Badge variant="primary" size="sm">Required</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {videoProgress.isCompleted && (
                      <span className="text-sm text-success-600 font-medium">
                        Completed {videoProgress.watchedAt && new Date(videoProgress.watchedAt).toLocaleDateString()}
                      </span>
                    )}
                    <Button
                      variant={isLocked ? 'secondary' : videoProgress.isCompleted ? 'success' : 'primary'}
                      onClick={() => setSelectedVideo(videoProgress)}
                      disabled={isLocked}
                      leftIcon={<PlayIcon className="h-4 w-4" />}
                    >
                      {videoProgress.isCompleted ? 'Watch Again' : 'Watch Video'}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          videoProgress={selectedVideo}
          onComplete={handleVideoComplete}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
};

export default TrainingDashboard;
